import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { Box, CardContent, Card } from "@mui/material";

import { Layout } from "Layout";

import { NEW_RECORD_SKELETON } from "appConfig";

import { CardGrid } from "components/CardGrid";
import { EditModalHeader } from "components/EditModalHeader";
import { FrmFieldGrid } from "components/FrmFieldGrid";
import { ObjectEditDialogContent } from "components/ObjectEditDialogContent";
import { CardHeader } from "components/formFields/formCards/CardHeader";
import { FrmUpsAccount } from "components/formFields/frm/FrmUpsAccount";
import { FixedLoadingIndicator } from "components/utility/FixedLoadingIndicator";
import { FormErrors } from "components/utility/FormErrors";
import { Loading } from "components/utility/Loading";
import { VSpace } from "components/utility/VSpace";

import { i18n } from "services/i18nService";
import { getRecord, getRecordFrom } from "services/sosInventoryService/sosApi";
import { isValidRates } from "services/sosInventoryService/upsShipment/isValid";
import { SHIPPING_TYPE_OPTIONS } from "services/sosInventoryService/upsShipment/schema";

import { useErrors } from "hooks/useErrors";
import { useRecord } from "hooks/useRecord";

import { PackageInfo } from "views/UpsShipments/UpsShipment/PackageInfo";
import { ShippingAddress } from "views/UpsShipments/UpsShipment/ShippingAddress";
import { ShippingOptions } from "views/UpsShipments/UpsShipment/ShippingOptions";
import { UpsRatesDialog } from "views/UpsShipments/UpsShipment/UpsRatesDialog";

import {
  editModalLoadingIndicatorOn,
  editModalLoadingIndicatorOff,
} from "globalState/loadingSlice";

import { INCH_UNIT, LB_UNIT } from "appConstants";

export function UpsShipment(props) {
  const { id, onClose, objectType, newFromId, newFromObjectType } = props;
  const [record, setRecord, handleInputFieldChange] = useRecord();
  const [showRatesDialog, setShowRatesDialog] = useState(false);

  // UI STATE
  const { errors, setErrors, isInError } = useErrors();

  const loadingState = useSelector((state) => state.loading.editModal);

  function selectRate(rate) {
    const { id } = SHIPPING_TYPE_OPTIONS.find(({ name }) => name === rate);
    handleInputFieldChange("serviceCode", id);
    setShowRatesDialog(false);
  }

  // OTHER SETUP
  const dispatch = useDispatch();

  useEffect(() => {
    async function _getRecord() {
      if (id) {
        dispatch(editModalLoadingIndicatorOn());
        const response = await getRecord(objectType, id);
        setRecord(response);
        dispatch(editModalLoadingIndicatorOff());
      } else if (newFromId) {
        // it's a "populate from" request
        const response = await getRecordFrom(
          objectType,
          newFromObjectType,
          newFromId
        );
        response.dimensionUnit = INCH_UNIT;
        response.weightUnit = LB_UNIT;
        delete response.id;
        setRecord(response);
      } else {
        const emptyRecord = NEW_RECORD_SKELETON[objectType]();
        setRecord(emptyRecord);
      }
    }
    _getRecord();
  }, [setRecord, id, objectType, dispatch, newFromId, newFromObjectType]);

  async function openRatesDialog() {
    const errors = isValidRates(record);
    if (errors.messages.length > 0) {
      setErrors(errors);
      return;
    }
    setShowRatesDialog(true);
  }

  function handleScalarChange(fieldName, value) {
    handleInputFieldChange(fieldName, value.id);
  }

  return (
    <>
      <EditModalHeader
        record={record}
        setRecord={setRecord}
        objectType={objectType}
        setErrors={setErrors}
        errors={errors}
        text={record?.number}
        handleClose={onClose}
      />
      {loadingState && <Loading />}
      <ObjectEditDialogContent>
        <Layout
          pageTitle={
            id
              ? `${i18n(`objectType.${objectType}.Sentence`)} ${
                  record?.number || ""
                }`
              : i18n(`objectType.${objectType}.New`)
          }
        >
          <Box sx={{ m: 2 }}>
            <FormErrors errors={errors} setErrors={setErrors} />
            {record ? (
              <>
                <FrmFieldGrid gridAutoFlow="dense">
                  <ShippingAddress
                    record={record}
                    addressType="fromAddress"
                    residentialAddressType="fromResidentialAddress"
                    title={i18n("frmLabel.ShipFrom")}
                    addressData={record.fromAddress}
                    onValueBlur={handleInputFieldChange}
                    isInError={isInError}
                    setRecord={setRecord}
                    location={record.location}
                  />
                  <ShippingAddress
                    record={record}
                    addressType="toAddress"
                    residentialAddressType="toResidentialAddress"
                    title={i18n("frmLabel.ShipTo")}
                    onValueBlur={handleInputFieldChange}
                    addressData={record.toAddress}
                    isInError={isInError}
                  />
                </FrmFieldGrid>
                <VSpace space={2} />
                <FrmFieldGrid gridAutoFlow="dense">
                  <PackageInfo
                    record={record}
                    onChange={handleInputFieldChange}
                    onScalarChange={handleScalarChange}
                    isInError={isInError}
                  />
                  <Box>
                    <ShippingOptions
                      record={record}
                      onChange={handleInputFieldChange}
                      onScalarChange={handleScalarChange}
                      openRatesDialog={openRatesDialog}
                      isInError={isInError}
                    />
                    <VSpace space={2} />
                    <Card variant="outlined">
                      <CardContent>
                        <CardHeader text={i18n("frmLabel.Payment")} />
                        <CardGrid>
                          <FrmUpsAccount
                            value={record.upsAccount}
                            onValueChange={handleInputFieldChange}
                            error={isInError("upsAccount")}
                          />
                        </CardGrid>
                      </CardContent>
                    </Card>
                  </Box>
                </FrmFieldGrid>
              </>
            ) : (
              <FixedLoadingIndicator text={`${i18n("global.Loading")}...`} />
            )}
          </Box>
        </Layout>
      </ObjectEditDialogContent>
      {showRatesDialog && (
        <UpsRatesDialog
          record={record}
          onClose={() => setShowRatesDialog(false)}
          selectRate={selectRate}
        />
      )}
    </>
  );
}
